import { lazy, useEffect, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Spinner } from '@newamsterdam/ui'
import { useUser } from './shared/store/user'
import { LoginView } from './shared/auth/LoginView'
import { CompanyGate } from './shared/auth/CompanyGate'
import { Shell } from './shared/layout/Shell'
import { Placeholder } from './shared/layout/Placeholder'

const ProjectListView = lazy(() =>
  import('./modules/budget/views/ProjectList').then((m) => ({ default: m.ProjectListView })),
)
const ProjectLayout = lazy(() =>
  import('./modules/budget/views/ProjectLayout').then((m) => ({ default: m.ProjectLayout })),
)
const BudgetTableView = lazy(() =>
  import('./modules/budget/views/BudgetTable').then((m) => ({ default: m.BudgetTableView })),
)
const CashflowView = lazy(() =>
  import('./modules/budget/views/Cashflow').then((m) => ({ default: m.CashflowView })),
)
const RealizationView = lazy(() =>
  import('./modules/budget/views/Realization').then((m) => ({ default: m.RealizationView })),
)
const AuditLogView = lazy(() =>
  import('./modules/admin/views/Audit').then((m) => ({ default: m.AuditLogView })),
)
const RoleManagementView = lazy(() =>
  import('./modules/admin/views/Roles').then((m) => ({ default: m.RoleManagementView })),
)
const UserListView = lazy(() =>
  import('./modules/admin/views/UserList').then((m) => ({ default: m.UserListView })),
)
const EmployeeListView = lazy(() =>
  import('./modules/people/views/EmployeeList').then((m) => ({ default: m.EmployeeListView })),
)
const EmployeeRatesView = lazy(() =>
  import('./modules/people/views/EmployeeRates').then((m) => ({ default: m.EmployeeRatesView })),
)
const EmployeeContractsView = lazy(() =>
  import('./modules/people/views/EmployeeContracts').then((m) => ({
    default: m.EmployeeContractsView,
  })),
)
const EmployeeAssignmentsView = lazy(() =>
  import('./modules/people/views/EmployeeAssignments').then((m) => ({
    default: m.EmployeeAssignmentsView,
  })),
)
const InvoiceListView = lazy(() =>
  import('./modules/finance/views/InvoiceList').then((m) => ({ default: m.InvoiceListView })),
)
const ContractorListView = lazy(() =>
  import('./modules/finance/views/ContractorList').then((m) => ({
    default: m.ContractorListView,
  })),
)
const BankAccountListView = lazy(() =>
  import('./modules/finance/views/BankAccountList').then((m) => ({
    default: m.BankAccountListView,
  })),
)
const BankAccountSnapshotsView = lazy(() =>
  import('./modules/finance/views/BankAccountSnapshots').then((m) => ({
    default: m.BankAccountSnapshotsView,
  })),
)
const LoanListView = lazy(() =>
  import('./modules/finance/views/LoanList').then((m) => ({ default: m.LoanListView })),
)
const LoanSnapshotsView = lazy(() =>
  import('./modules/finance/views/LoanSnapshots').then((m) => ({
    default: m.LoanSnapshotsView,
  })),
)
const ImportListView = lazy(() =>
  import('./modules/finance/views/ImportList').then((m) => ({ default: m.ImportListView })),
)
const ImportItemsView = lazy(() =>
  import('./modules/finance/views/ImportItems').then((m) => ({ default: m.ImportItemsView })),
)
const AdvancesView = lazy(() =>
  import('./modules/finance/views/Advances').then((m) => ({ default: m.AdvancesView })),
)
const CompanyCostsView = lazy(() =>
  import('./modules/finance/views/CompanyCosts').then((m) => ({ default: m.CompanyCostsView })),
)
const AdminCostCategoriesView = lazy(() =>
  import('./modules/admin/views/CostCategories').then((m) => ({ default: m.AdminCostCategoriesView })),
)
const AdminCompaniesView = lazy(() =>
  import('./modules/admin/views/Companies').then((m) => ({ default: m.AdminCompaniesView })),
)
const ProjectAccessView = lazy(() =>
  import('./modules/admin/views/ProjectAccess').then((m) => ({ default: m.ProjectAccessView })),
)
const AdministrationView = lazy(() =>
  import('./modules/admin/views/Administration').then((m) => ({ default: m.AdministrationView })),
)
const SalesForecastView = lazy(() =>
  import('./modules/sales/views/SalesForecast').then((m) => ({ default: m.SalesForecastView })),
)

function ModuleGuard({ module, children }: { module: string; children: ReactNode }) {
  const modules = useUser((s) => s.modules)
  if (!modules.includes(module)) {
    return <Placeholder title="Brak dostępu" hint={`Moduł ${module} nie jest dostępny dla tego konta`} />
  }
  return <>{children}</>
}

function PermissionGuard({
  permissions: required,
  children,
}: {
  permissions: string[]
  children: ReactNode
}) {
  const userPermissions = useUser((s) => s.permissions)
  const ok = required.some((p) => userPermissions.includes(p))
  if (!ok) {
    return <Placeholder title="Brak uprawnień" hint="Skontaktuj się z administratorem" />
  }
  return <>{children}</>
}

export function App() {
  const loaded = useUser((s) => s.loaded)
  const id = useUser((s) => s.id)
  const load = useUser((s) => s.load)

  useEffect(() => {
    void load()
  }, [load])

  if (!loaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Spinner />
      </div>
    )
  }

  if (!id) return <LoginView />

  return (
    <CompanyGate>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Navigate to="/projects" replace />} />
          <Route path="/projects" element={<ModuleGuard module="budget"><ProjectListView /></ModuleGuard>} />
          <Route
            path="/projects/:projectId"
            element={
              <ModuleGuard module="budget">
                <ProjectLayout />
              </ModuleGuard>
            }
          >
            <Route index element={<Navigate to="budget" replace />} />
            <Route path="budget" element={<BudgetTableView />} />
            <Route
              path="realizations"
              element={
                <PermissionGuard permissions={['view_costs', 'view_all']}>
                  <RealizationView />
                </PermissionGuard>
              }
            />
            <Route path="cashflow" element={<Navigate to="../budget" replace />} />
            <Route path="retention" element={<Navigate to="../budget" replace />} />
          </Route>
          <Route
            path="/cashflow"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard permissions={['view_company_finance', 'view_all']}>
                  <CashflowView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/sales/forecast"
            element={
              <ModuleGuard module="budget">
                <PermissionGuard permissions={['view_sales_forecast']}>
                  <SalesForecastView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/projects/:projectId/access"
            element={
              <ModuleGuard module="budget">
                <PermissionGuard permissions={['manage_projects', 'view_all']}>
                  <ProjectAccessView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ModuleGuard module="admin">
                <PermissionGuard permissions={['manage_users', 'view_all']}>
                  <UserListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ModuleGuard module="admin">
                <PermissionGuard permissions={['manage_users', 'view_all']}>
                  <RoleManagementView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/administration"
            element={
              <ModuleGuard module="admin">
                <AdministrationView />
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ModuleGuard module="admin">
                <PermissionGuard permissions={['view_audit_log', 'view_all']}>
                  <AuditLogView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/cost-categories"
            element={
              <ModuleGuard module="admin">
                <PermissionGuard permissions={['manage_company_costs', 'view_all']}>
                  <AdminCostCategoriesView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <ModuleGuard module="admin">
                <PermissionGuard permissions={['manage_users', 'view_all']}>
                  <AdminCompaniesView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/people/employees"
            element={
              <ModuleGuard module="people">
                <PermissionGuard permissions={['manage_employees', 'view_all']}>
                  <EmployeeListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/people/employees/:employeeId/rates"
            element={
              <ModuleGuard module="people">
                <PermissionGuard permissions={['manage_employees', 'view_all']}>
                  <EmployeeRatesView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/people/employees/:employeeId/assignments"
            element={
              <ModuleGuard module="people">
                <PermissionGuard permissions={['manage_employees', 'view_all']}>
                  <EmployeeAssignmentsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/people/employees/:employeeId/contracts"
            element={
              <ModuleGuard module="people">
                <PermissionGuard permissions={['manage_employees', 'view_all']}>
                  <EmployeeContractsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route path="/people/rates" element={<Placeholder title="Stawki" hint="m1.5 — People" />} />
          <Route
            path="/finance/invoices"
            element={
              <ModuleGuard module="finance">
                <PermissionGuard permissions={['manage_invoices', 'view_all']}>
                  <InvoiceListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/contractors"
            element={
              <ModuleGuard module="finance">
                <PermissionGuard permissions={['manage_contractors', 'view_all']}>
                  <ContractorListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/bank-accounts"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard
                  permissions={['manage_bank_accounts', 'view_company_finance', 'view_all']}
                >
                  <BankAccountListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/bank-accounts/:accountId/snapshots"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard
                  permissions={['manage_bank_accounts', 'view_company_finance', 'view_all']}
                >
                  <BankAccountSnapshotsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/loans"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard
                  permissions={['manage_loans', 'view_company_finance', 'view_all']}
                >
                  <LoanListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/loans/:id"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard
                  permissions={['manage_loans', 'view_company_finance', 'view_all']}
                >
                  <LoanSnapshotsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/imports"
            element={
              <ModuleGuard module="finance">
                <PermissionGuard permissions={['manage_imports', 'view_all']}>
                  <ImportListView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/imports/:batchId/items"
            element={
              <ModuleGuard module="finance">
                <PermissionGuard permissions={['manage_imports', 'view_all']}>
                  <ImportItemsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/advances"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard permissions={['manage_advances', 'view_all']}>
                  <AdvancesView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/finance/company-costs"
            element={
              <ModuleGuard module="cash_position">
                <PermissionGuard permissions={['manage_company_costs', 'view_all']}>
                  <CompanyCostsView />
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="/settings"
            element={<Placeholder title="Ustawienia" hint="Zmiana hasła i preferencje konta (#182)" />}
          />
          <Route path="*" element={<Placeholder title="404" hint="Nie znaleziono" />} />
        </Route>
      </Routes>
    </CompanyGate>
  )
}
